import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/contexts/UserContext';
import { useSwitchOrganizationContext } from '@/domains/accounts/services/account-context-switch';
import { Organization } from '@/shared/types/userTypes';
import { showErrorWithApiError } from '@/shared/utils/toast-utils';
import { ERROR } from '@/config/toast-message-config';

/**
 * Account context switching helpers.
 * Used by TopBar (tenant users) and admin list pages (account pick).
 */
export function useOrganizationContextSwitch() {
  const queryClient = useQueryClient();
  const { setSelectedOrganizationOnly, refreshAuth } = useUser();
  const { mutateAsync, isPending } = useSwitchOrganizationContext();

  const switchOrganizationContext = useCallback(
    async (organization: Organization, parentOrganization?: Organization | null) => {
      try {
        await mutateAsync({
          organizationId: organization.organizationId,
          parentOrganizationId: parentOrganization?.organizationId ?? undefined,
        });
        await refreshAuth();

        // Flush selection before query invalidation so customer pages don't refetch
        // with the previous customerId under the new session token.
        flushSync(() => {
          setSelectedOrganizationOnly(organization, parentOrganization ?? null);
        });

        await queryClient.invalidateQueries();
        return true;
      } catch (error) {
        showErrorWithApiError(error, ERROR.GENERIC.ACCOUNT_SWITCH_FAILED);
        return false;
      }
    },
    [mutateAsync, refreshAuth, queryClient, setSelectedOrganizationOnly],
  );

  return { switchOrganizationContext, isSwitchingOrganizationContext: isPending };
}
